import { useState, useEffect } from 'react';
import { supabaseGetter } from '~/api/supabase-getter';
import _ from 'lodash';

/**
 * Hook to fetch crop phase types for multiple propagation codes
 * Returns the UNION of all phase types applicable to any of the propagation methods
 *
 * @param {Object} params
 * @param {string[]} params.propagationCodes - Array of propagation codes
 * @returns {Object} Merged crop phase types, loading state, and error
 */
export const useCropPhaseTypesForPropagations = ({ propagationCodes }) => {
  const [cropPhaseTypes, setCropPhaseTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Early return if no propagation codes
    if (!propagationCodes || propagationCodes.length === 0) {
      setCropPhaseTypes([]);
      setLoading(false);
      setError(null);
      return;
    }

    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        // Fetch phase types for each propagation code in parallel
        const phaseTypePromises = propagationCodes.map(code =>
          supabaseGetter.cropPhaseTypes.getByPropagationType(code)
        );

        const results = await Promise.all(phaseTypePromises);

        // Check for errors
        const errors = results.filter(r => r.error);
        if (errors.length > 0) {
          throw errors[0].error;
        }

        // Merge all phase types and deduplicate by code
        const allPhaseTypes = results.flatMap(r => r.data || []);
        const uniquePhaseTypes = _.uniqBy(allPhaseTypes, 'code');

        // Sort by typical_duration_days to maintain consistent ordering
        const sortedPhaseTypes = _.sortBy(uniquePhaseTypes, 'typical_duration_days');

        setCropPhaseTypes(sortedPhaseTypes);
      } catch (err) {
        console.error('[useCropPhaseTypesForPropagations] Error fetching crop phase types:', err);
        setError(err.message);
        setCropPhaseTypes([]);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [propagationCodes]);

  return {
    cropPhaseTypes,
    loading,
    error,
    invalidate: () => {
      supabaseGetter.cropPhaseTypes.invalidate();
    }
  };
};
